import React, { type ReactElement } from "react";
import {
  FloatingContext,
  FloatingContextItem,
  FloatingContextLabel,
  FloatingContextSeparator,
} from "@/components/ui/floating-context";
import { ConfirmDeleteDialog } from "@/components/dialogs/ConfirmDeleteDialog";
import { useResourceActions } from "@/hooks/resources/useResourceActions";
import { getResourceInfo } from "@/utilities/getResourceInfo";
import type { Resource } from "@/lib/types";
import { SCHEMA_NAMES, type SchemaNames } from "instantdb/types";
import { CommonIcon } from "@/icons/CommonIcon";
import { useMobile } from "@/providers/mobileProvider";
import { useTranslation } from "@/hooks/useTranslation";

type ResourceContextMenuProps = {
  slug: SchemaNames;
  resource: Resource;
  trigger: ReactElement;
  triggerClassName?: string;
  enabled?: boolean;
};

/**
 * Generic context menu for resources
 */
export const ResourceContextMenu = ({
  slug,
  resource,
  trigger,
  triggerClassName,
  enabled = true,
}: ResourceContextMenuProps) => {
  const { labels } = getResourceInfo(slug);
  const { isAndroid } = useMobile();
  const { t, tNode } = useTranslation();

  const { openConfirmDeleteDialog, setConfirmDeleteDialogOpen, actions, handleDelete } =
    useResourceActions(slug, resource);

  if (!enabled || isAndroid) return <div className={triggerClassName}>{trigger}</div>;

  const title = slug === SCHEMA_NAMES.healthAreas ? t(resource.title) : resource.title;

  return (
    <>
      <ConfirmDeleteDialog
        description={
          <span>
            {tNode("resource_context_menu.delete_confirmation", {
              resource: labels.singular.toLowerCase(),
              title: <strong>{title}</strong>,
            })}
          </span>
        }
        open={openConfirmDeleteDialog}
        onOpenChange={setConfirmDeleteDialogOpen}
        onConfirm={handleDelete}
      />

      <FloatingContext trigger={trigger} triggerClassName={triggerClassName}>
        <FloatingContextLabel className="capitalize">
          {t("resource_context_menu.actions", { resource: labels.singular })}
        </FloatingContextLabel>

        <FloatingContextSeparator />

        {actions.flatMap((action) =>
          [
            <FloatingContextItem
              key={action.label}
              label={action.label}
              onClick={action.onClick}
              disabled={action.disabled}
            >
              <CommonIcon name={action.icon} className="mr-3 size-4" />
            </FloatingContextItem>,
            action.separator && <FloatingContextSeparator key={`${action.label}-separator`} />,
          ].filter(Boolean),
        )}
      </FloatingContext>
    </>
  );
};
